/**
 * Phase 7e — last-edited section persistence (per-device).
 *
 * Stores the most recently navigated `currentSectionId` in
 * `localStorage`, keyed by `applicationId`, so a returning applicant
 * lands on the section they were last editing instead of always at
 * `ORDERED_SECTIONS[0]`.
 *
 * Why localStorage rather than Firestore:
 *   - This is a per-device UI state ("which tab in the nav am I on")
 *     and never needs to round-trip the wire. The auto-save layer
 *     already preserves the *answers* in Firestore; restoring nav
 *     position belongs on the device.
 *   - No schema change to the backend, no extra round-trip on App
 *     mount, no cross-device drift to reconcile.
 *
 * Storage shape: a single string per key
 *   `inside-us-aos:last-section:<applicationId>` → `<sectionId>`.
 *
 * Validation of the stored value (e.g., "is this still a real
 * section?") is the caller's responsibility — the App.tsx hook
 * cross-checks against `ORDERED_SECTIONS` and the review-leaf id set
 * before adopting the stored value.
 */

const KEY_PREFIX = 'inside-us-aos:last-section:';

function storage(): Storage | null {
  if (typeof globalThis === 'undefined') return null;
  if (!('localStorage' in globalThis)) return null;
  try {
    return (globalThis as unknown as Window).localStorage;
  } catch {
    return null;
  }
}

function makeKey(applicationId: string): string {
  return `${KEY_PREFIX}${applicationId}`;
}

/**
 * Returns the previously stored sectionId for the given application,
 * or `null` when nothing has been stored, when storage is
 * inaccessible, or when an exception is thrown by the storage
 * implementation (private-mode browsers, quota errors, etc.).
 */
export function getLastSectionId(applicationId: string): string | null {
  const s = storage();
  if (s === null) return null;
  try {
    return s.getItem(makeKey(applicationId));
  } catch {
    return null;
  }
}

/**
 * Persists `sectionId` as the last-edited section for the given
 * application. Failures (quota, disabled storage) are swallowed —
 * the next navigation overwrites the slot anyway, so a one-off write
 * miss is recoverable.
 */
export function setLastSectionId(
  applicationId: string,
  sectionId: string,
): void {
  const s = storage();
  if (s === null) return;
  try {
    s.setItem(makeKey(applicationId), sectionId);
  } catch {
    // Ignored — see docstring.
  }
}

/**
 * Removes the stored sectionId for the given application. Used by
 * tests today; surfaces the affordance for a future "start over"
 * action without forcing callers to know the key shape.
 */
export function clearLastSectionId(applicationId: string): void {
  const s = storage();
  if (s === null) return;
  try {
    s.removeItem(makeKey(applicationId));
  } catch {
    // Ignored — see docstring.
  }
}
